import { useTranslation } from "react-i18next";
import { useState } from "react";

const Contact = () => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    company: "",
    phone: "",
    email: "",
    message: "",
  });

  const [emailError, setEmailError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const validateEmail = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setEmailError(emailRegex.test(value) ? "" : t("contact.invalid_email"));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "email") validateEmail(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (emailError) return;

    const res = await fetch("https://api.web3forms.com/submit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        access_key: "86355b91-4d8e-426f-8919-a5fdeb38d0e4",
        ...formData,
        subject: "Neue Kontaktanfrage",
      }),
    });

    if (res.ok) {
      setSubmitted(true);
      setFormData({
        firstName: "",
        lastName: "",
        company: "",
        phone: "",
        email: "",
        message: "",
      });
    }
  };

  return (
      <section className="px-4 md:px-8 max-w-3xl mx-auto mt-20 mb-20">
        <h2 className="text-2xl font-bold text-dark-primary text-center md:pt-8 mb-4">
          {t("contact.title")}
        </h2>

        {/* Notice for B2B and authorities only */}
        <div className="mb-4 p-4 rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/20 text-amber-800 dark:text-amber-200 text-sm leading-relaxed">
          <p className="font-semibold mb-1">
            ⚠️ {t("contact.notice_heading", "Wichtiger Hinweis:")}
          </p>
          <p>
            {t("contact.notice_text", "Dieses Kontaktformular steht ausschließlich Unternehmen und Behörden zur Verfügung. Kundenanfragen können hierüber nicht beantwortet werden. Bitte nutzen Sie für Support- und Kundenanfragen unseren Live-Chat.")}
          </p>
        </div>

        {submitted ? (
            <p className="text-center text-green-600 dark:text-green-400 font-semibold">
              ✅ Nachricht wurde erfolgreich gesendet!
            </p>
        ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* Name Row */}
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    {t("contact.first_name")}
                  </label>
                  <input
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      type="text"
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-zinc-800 text-gray-900 dark:text-white"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    {t("contact.last_name")}
                  </label>
                  <input
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      type="text"
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-zinc-800 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Firma (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  {t("contact.company")} <span className="text-sm text-gray-500">({t("contact.optional")})</span>
                </label>
                <input
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    type="text"
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-zinc-800 text-gray-900 dark:text-white"
                />
              </div>

              {/* Telefon (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  {t("contact.phone")} <span className="text-sm text-gray-500">({t("contact.optional")})</span>
                </label>
                <input
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    type="tel"
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-zinc-800 text-gray-900 dark:text-white"
                />
              </div>

              {/* E-Mail */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  {t("contact.email")}
                </label>
                <input
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    type="email"
                    className={`w-full px-4 py-2 rounded-lg border ${
                        emailError ? "border-red-500" : "border-gray-300 dark:border-gray-700"
                    } bg-white dark:bg-zinc-800 text-gray-900 dark:text-white`}
                />
                {emailError && (
                    <p className="text-sm text-red-500 mt-1">{emailError}</p>
                )}
              </div>

              {/* Nachricht */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  {t("contact.message")}
                </label>
                <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-zinc-800 text-gray-900 dark:text-white"
                />
              </div>

              {/* Button */}
              <div className="">
                <button
                    type="submit"
                    className="w-full md:w-auto px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {t("contact.send_placeholder")}
                </button>
              </div>
            </form>
        )}
      </section>
  );
};

export default Contact;